import { CompanyReadDataProvider } from '@aibees/elephant/context';
import { SearchType } from '@aibees/elephant/types';
import { useBoolean } from '@aibees/shared/hooks';
import { useTableSearchItemStore } from '@aibees/shared/store/table-search-item';
import { Box, Stack } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { useSearchFiltersStore } from '../search-filters/store';
import { SidebarLookalike } from '../sidebar-lookalike/sidebar-lookalike';
import { Tab, TabDrawer } from '../tab-drawer';
import { CompanyDescriptionSeoWrapper } from './components/company-description-seo-wrapper';
import { CompanySummary } from './components/company-summary';
import { HeaderNavigation } from './components/header-navigation';
import { RecordManagement } from './components/record-management';
import { Employees } from './feature/employees';
import { Insight } from './feature/insight';
import { Overview } from './feature/overview';
import { CompanyOverview } from './feature/overview/components/company-overview';
import * as SX from './sx';
import { SearchProvider } from '@aibees/shared/context';

export const SearchDrawer = memo(() => {
  const state = useBoolean(false);
  const currentItemId = useSearchFiltersStore.useSelectedItemId?.();
  const { setSelectedItem } = useTableSearchItemStore();

  const setCurrentItemId = useSearchFiltersStore.useSetSelectedItemId();
  const setRecordManagementOpen =
    useSearchFiltersStore.useSetRecordManagementOpen();
  const setViewAllLists = useSearchFiltersStore.useSetViewAllLists();
  const setViewAllLabels = useSearchFiltersStore.useSetViewAllLabels();

  useEffect(() => {
    if (!state.value) {
      setViewAllLists(false);
      setViewAllLabels(false);
      setSelectedItem(undefined);
      setTabs((prev) =>
        prev.map((tab) => ({
          ...tab,
          active: tab.title === 'Overview',
        }))
      );
    }
  }, [state.value]);

  useEffect(() => {
    if (currentItemId) {
      state.setValue(true);
    } else {
      state.setValue(false);
      setRecordManagementOpen(false);
    }
  }, [currentItemId]);

  const [tabs, setTabs] = useState<Tab[]>([
    {
      title: 'Overview',
      icon: 'dashboard-outline',
      active: true,
      disabled: false,
      component: <Overview />,
    },
    {
      title: 'Insight ',
      description: '(Coming Soon)',
      icon: 'Light-bulb-outline',
      active: false,
      disabled: true,
      comingSoon: true,
      component: <Insight />,
    },
    {
      title: 'Lookalike',
      icon: 'fingerprint',
      active: false,
      disabled: false,
      component: <SidebarLookalike />,
    },
    {
      title: 'Employees',
      icon: 'people',
      active: false,
      disabled: false,
      component: <Employees />,
    },
    {
      title: 'Update ',
      description: '(Coming Soon)',
      icon: 'bell-ring-outline',
      active: false,
      disabled: true,
      comingSoon: true,
      component: <>Update</>,
    },
  ]);

  const handleChangeTabs = (tab: Tab) => {
    if (tab.disabled) return;
    setTabs((prev) => {
      return prev.map((t) =>
        t.title === tab.title ? { ...t, active: true } : { ...t, active: false }
      );
    });
  };

  const activeTab = tabs.find((i) => i.active);
  return (
    <CompanyReadDataProvider id={currentItemId}>
      <div
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <SearchProvider>
          <TabDrawer
            className="SearchDrawerTabDrawer"
            state={state}
            header={<HeaderNavigation drawerState={state} />}
            handleChangeTabs={handleChangeTabs}
            subHeader={
              <>
                <CompanySummary />
                {activeTab?.title !== 'Lookalike' &&
                  activeTab?.title !== 'Employees' && (
                    <>
                      <CompanyDescriptionSeoWrapper />
                      <Stack gap={1.5} sx={SX.box}>
                        <Box>
                          <RecordManagement
                            id={currentItemId}
                            type={SearchType.Company}
                          />
                        </Box>
                        <Box>
                          <CompanyOverview />
                        </Box>
                      </Stack>
                    </>
                  )}
              </>
            }
            tabs={tabs}
            tabContent={activeTab?.component ?? <div />}
            onClose={setCurrentItemId}
            width={497}
          />
        </SearchProvider>
      </div>
    </CompanyReadDataProvider>
  );
});
SearchDrawer.displayName = 'SearchDrawer';
